/**
 * Outreach Dashboard – summary stats with email performance metrics.
 */
import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Spin, Typography, Divider, Tag, Space, Alert, Table, Button, message } from 'antd';
import {
  UserOutlined,
  MailOutlined,
  SendOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { outreachCampaignsApi, outreachSenderAccountsApi } from '../api';
import type { DashboardStats, SenderAccountStatsItem, DomainStatsItem } from '../types';
import { useTheme } from '../../../contexts/ThemeContext';

const { Title, Text } = Typography;

const cardStyle: React.CSSProperties = {
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  height: '100%',
};

export const OutreachDashboard: React.FC = () => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const data = await outreachCampaignsApi.dashboardStats();
      setStats(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch outreach stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    message.loading({ content: 'Scanning inboxes for bounces and unsubscribes...', key: 'sync' });
    try {
      // API returns active senders only; list is used as-is
      const accountsRes = await outreachSenderAccountsApi.list();
      const activeAccounts = accountsRes.items;
      
      if (activeAccounts.length === 0) {
        message.warning({ content: 'No active sender accounts found to sync', key: 'sync' });
        setSyncing(false);
        return;
      }

      // 2. Loop through and trigger sync for each (sequentially to avoid overlapping Gmail login tabs)
      const { outreachInboxApi } = await import('../api/inbox');
      
      // One-time full inbox rescan after parser/sync improvements (then incremental only)
      const fullRescanKey = 'outreach_inbox_full_rescan_v3';
      const runFullRescan = localStorage.getItem(fullRescanKey) !== 'done';

      for (const account of activeAccounts) {
        try {
          const modeLabel = runFullRescan ? 'full rescan' : 'incremental';
          message.loading({ content: `Syncing ${account.email} (${modeLabel})...`, key: 'sync' });
          await outreachInboxApi.processBounces({
            sender_account_id: account.id,
            max_messages: runFullRescan ? 15000 : 2000,
            incremental: true,
            full_sync: runFullRescan,
          });
          await outreachInboxApi.processUnsubscribes({
            sender_account_id: account.id,
            max_messages: runFullRescan ? 1000 : 200,
            incremental: true,
            full_sync: runFullRescan,
          });
        } catch (e) {
          console.error(`Sync failed for ${account.email}:`, e);
        }
      }

      if (runFullRescan) {
        localStorage.setItem(fullRescanKey, 'done');
      }

      // Apply server-side known bounce registry (covers Gmail misses / case duplicates)
      try {
        message.loading({ content: 'Applying known bounce & unsubscribe lists...', key: 'sync' });
        const bulk = await outreachInboxApi.bulkSuppressBounces();
        const unsub = await outreachInboxApi.bulkUnsubscribe();
        console.info('Known bounce bulk suppress:', bulk);
        console.info('Known unsubscribe bulk apply:', unsub);
      } catch (e) {
        console.error('Bulk bounce/unsubscribe apply failed:', e);
      }

      // 3. Refresh stats
      await fetchStats(false);
      message.success({ content: 'Dashboard updated with latest events from Gmail', key: 'sync', duration: 3 });
    } catch (err) {
      console.error('Sync failed:', err);
      message.error({ content: 'Failed to sync latest data', key: 'sync' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
        <Spin size="large" tip="Loading statistics..." />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message={error || 'Failed to load statistics'}
          showIcon
          action={<Button size="small" onClick={() => fetchStats()}>Retry</Button>}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>Outreach Performance</Title>
        <Space>
          <Button
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSync}
            loading={syncing}
          >
            Refresh Metrics
          </Button>
        </Space>
      </div>

      {/* Overview stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} bodyStyle={{ padding: '20px 24px' }}>
            <Statistic
              title="Leads"
              value={stats.active_leads}
              prefix={<UserOutlined style={{ color: '#1890ff', marginRight: 8 }} />}
            />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {stats.total_leads - stats.active_leads} inactive
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} bodyStyle={{ padding: '20px 24px' }}>
            <Statistic
              title="Campaigns"
              value={stats.total_campaigns}
              prefix={<MailOutlined style={{ color: '#52c41a', marginRight: 8 }} />}
            />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {stats.active_campaigns} currently active
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} bodyStyle={{ padding: '20px 24px' }}>
            <Statistic
              title="Emails Sent"
              value={stats.total_sent}
              prefix={<SendOutlined style={{ color: '#722ed1', marginRight: 8 }} />}
            />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Successfully dispatched to Gmail
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={cardStyle} bodyStyle={{ padding: '20px 24px' }}>
            <Statistic
              title="Sender Accounts"
              value={stats.sender_accounts}
              prefix={<CheckCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />}
            />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Connected Gmail accounts
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Email Performance – 3-tier breakdown */}
        <Col xs={24} lg={16}>
          <Card title="Email Performance" style={cardStyle}>
            {/* ── Combined (All Accounts) – big percentages ── */}
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic
                  title="Delivery Rate"
                  value={stats.delivery_rate}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: '#52c41a' }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>{stats.total_delivered} of {stats.total_sent} delivered</Text>
              </Col>
              <Col span={8}>
                <Statistic
                  title="Bounce Rate"
                  value={stats.bounce_rate}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: '#ff4d4f' }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>{stats.total_bounced} bounced</Text>
              </Col>
              <Col span={8}>
                <Statistic
                  title="Unsubscribe Rate"
                  value={stats.unsubscribe_rate}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: '#faad14' }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>{stats.total_unsubscribed} unsubscribed</Text>
              </Col>
            </Row>

            <Divider style={{ margin: '24px 0 16px' }} />

            {/* ── Domain Level ── */}
            <Title level={5} style={{ fontSize: 14, marginBottom: 8 }}>By Domain</Title>
            <Table<DomainStatsItem>
              dataSource={stats.domain_stats || []}
              rowKey="domain"
              pagination={false}
              size="small"
              style={{ marginBottom: 8 }}
              columns={[
                {
                  title: 'Domain',
                  dataIndex: 'domain',
                  render: (v: string) => <Text strong>@{v}</Text>,
                },
                {
                  title: 'Sent',
                  dataIndex: 'total_sent',
                  width: 70,
                  align: 'center' as const,
                },
                {
                  title: 'Bounce Rate',
                  key: 'bounce',
                  width: 120,
                  align: 'center' as const,
                  render: (_: any, r: DomainStatsItem) => (
                    <span>
                      <Tag color="red" style={{ margin: 0 }}>{r.bounce_rate}%</Tag>
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>({r.total_bounced})</Text>
                    </span>
                  ),
                },
                {
                  title: 'Unsub Rate',
                  key: 'unsub',
                  width: 120,
                  align: 'center' as const,
                  render: (_: any, r: DomainStatsItem) => (
                    <span>
                      <Tag color="orange" style={{ margin: 0 }}>{r.unsubscribe_rate}%</Tag>
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>({r.total_unsubscribed})</Text>
                    </span>
                  ),
                },
              ]}
            />

            <Divider style={{ margin: '16px 0' }} />

            {/* ── Individual Sender Accounts ── */}
            <Title level={5} style={{ fontSize: 14, marginBottom: 8 }}>By Sender Account</Title>
            <Table<SenderAccountStatsItem>
              dataSource={stats.sender_account_stats || []}
              rowKey="sender_account_id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Sender',
                  key: 'sender',
                  render: (_: any, r: SenderAccountStatsItem) => (
                    <div>
                      <Text strong style={{ fontSize: 13 }}>{r.display_name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>{r.email}</Text>
                    </div>
                  ),
                },
                {
                  title: 'Sent',
                  dataIndex: 'total_sent',
                  width: 70,
                  align: 'center' as const,
                },
                {
                  title: 'Bounce Rate',
                  key: 'bounce',
                  width: 120,
                  align: 'center' as const,
                  render: (_: any, r: SenderAccountStatsItem) => (
                    <span>
                      <Tag color="red" style={{ margin: 0 }}>{r.bounce_rate}%</Tag>
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>({r.total_bounced})</Text>
                    </span>
                  ),
                },
                {
                  title: 'Unsub Rate',
                  key: 'unsub',
                  width: 120,
                  align: 'center' as const,
                  render: (_: any, r: SenderAccountStatsItem) => (
                    <span>
                      <Tag color="orange" style={{ margin: 0 }}>{r.unsubscribe_rate}%</Tag>
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>({r.total_unsubscribed})</Text>
                    </span>
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        {/* Lead Health & Suppression */}
        <Col xs={24} lg={8}>
          <Card title="Lead Suppression" style={cardStyle}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text><CloseCircleOutlined style={{ color: '#faad14', marginRight: 8 }} /> Bounced</Text>
                <Tag color="gold">{stats.bounced_leads}</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text><StopOutlined style={{ color: '#ff4d4f', marginRight: 8 }} /> Unsubscribes</Text>
                <Tag color="red">{stats.do_not_send_leads}</Tag>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <Statistic
                title="Total Blocked"
                value={stats.bounced_leads + stats.do_not_send_leads}
                valueStyle={{ color: '#ff7a45', fontSize: 24 }}
              />

              <div style={{ 
                padding: '12px', 
                background: isDark ? '#1f1f1f' : '#f9f9f9', 
                borderRadius: 8, 
                fontSize: 12,
                border: isDark ? '1px solid #434343' : 'none'
              }}>
                <Text type="secondary">
                  Blocked leads are automatically filtered out from all future campaigns to protect your sender reputation.
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
